/**
 * Settings Page
 *
 * Main page component for API Key management
 * Orchestrates table and modal components
 * Updated for API Key Rotation feature with confirmation dialog
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'equal-ds-ui';
import { AlertTriangle, X, CheckCircle, Copy } from 'lucide-react';
import { ApiKeysTable } from './ApiKeysTable';
import { ApiKeyModal } from './ApiKeyModal';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { settingsAPI } from '../../../services/settings-api.service';
import type { SettingsPageProps, ApiKeyData } from '../../../types/settings';

export const SettingsPage: React.FC<SettingsPageProps> = () => {

  // State
  const [apiKeys, setApiKeys] = useState<ApiKeyData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<ApiKeyData | null>(null);
  const [showGenerateButton, setShowGenerateButton] = useState(false);
  const [showActions, setShowActions] = useState(false);

  // API Key Rotation: Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedKeyForRegenerate, setSelectedKeyForRegenerate] = useState<ApiKeyData | null>(null);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // API Key Rotation config from sessionStorage (set by browserInfo API)
  const [apiKeyConfig, setApiKeyConfig] = useState({
    default_validity_days: 365,
    grace_period_days: 7
  });

  // Error handling
  const { handleError } = useErrorHandler();

  // Show toast notification
  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  // Copy to clipboard handler
  const handleCopyOrgId = useCallback(() => {
    const orgId = apiKeys[0]?.organizationId;
    if (orgId) {
      navigator.clipboard.writeText(orgId);
      showToast('Organization ID copied to clipboard', 'success');
    }
  }, [apiKeys, showToast]);

  // Check user role for permissions and load API key rotation config
  useEffect(() => {
    const userInfoEncoded = sessionStorage.getItem('loggedUserInfo');
    if (userInfoEncoded) {
      try {
        const userInfo = JSON.parse(atob(userInfoEncoded));
        const userRole = userInfo?.role;
        setShowActions(userRole === 'superadmin');
      } catch {
        setShowActions(false);
      }
    }

    // Load API key rotation config from sessionStorage (set by browserInfo API)
    const configString = sessionStorage.getItem('apiKeyRotationConfig');
    if (configString) {
      try {
        const config = JSON.parse(configString);
        setApiKeyConfig({
          default_validity_days: config.default_validity_days || 365,
          grace_period_days: config.grace_period_days || 7
        });
      } catch {
        // Use defaults if parsing fails: default_validity_days=365, grace_period_days=7
        // These defaults are already set in the initial state above
      }
    }
  }, []);

  // Fetch API keys on mount
  useEffect(() => {
    fetchApiKeys();
  }, []);

  // Fetch API key details
  const fetchApiKeys = useCallback(async () => {
    setIsLoading(true);
    setShowGenerateButton(false);

    try {
      const response = await settingsAPI.getKeyDetails({ all: 'true' });

      if (response.status === 'success' && response.data) {
        // Check if data is empty string or has count of 0
        if (typeof response.data === 'string' || response.data.count === 0) {
          setShowGenerateButton(true);
          setApiKeys([]);
        } else {
          setShowGenerateButton(false);
          setApiKeys(response.data.rows || []);
        }
      } else {
        setShowGenerateButton(true);
        setApiKeys([]);
      }
    } catch (error) {
      handleError(error, { component: 'SettingsPage', action: 'fetchApiKeys' });
      setShowGenerateButton(true);
      setApiKeys([]);
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  // Generate new API key
  const handleGenerateKey = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await settingsAPI.generateKey();

      if (response.status === 'success' && response.message) {
        setGeneratedKey(response.message);
        setShowModal(true);
        // Refresh the list after generating
        await fetchApiKeys();
      }
    } catch (error) {
      handleError(error, { component: 'SettingsPage', action: 'generateKey' });
    } finally {
      setIsLoading(false);
    }
  }, [handleError, fetchApiKeys]);

  // API Key Rotation: Open confirmation dialog before regenerating
  const handleRegenerateKey = useCallback((apiKey: ApiKeyData) => {
    // Don't allow regenerating GRACE status keys
    if (apiKey.status === 'GRACE') {
      showToast('Cannot regenerate a key that is in grace period', 'error');
      return;
    }
    setSelectedKeyForRegenerate(apiKey);
    setShowConfirmDialog(true);
  }, [showToast]);

  // API Key Rotation: Cancel regeneration
  const handleCancelRegenerate = useCallback(() => {
    setShowConfirmDialog(false);
    setSelectedKeyForRegenerate(null);
  }, []);

  // API Key Rotation: Confirm and perform regeneration
  const handleConfirmRegenerate = useCallback(async () => {
    if (!selectedKeyForRegenerate) return;

    setShowConfirmDialog(false);
    setIsLoading(true);

    try {
      const response = await settingsAPI.regenerateKey({ id: selectedKeyForRegenerate.id });

      if (response.status === 'success' && response.data) {
        setGeneratedKey(response.data);
        setShowModal(true);
        showToast('API Key regenerated successfully', 'success');
        // Refresh the list after regenerating
        await fetchApiKeys();
      }
    } catch (error: unknown) {
      handleError(error, { component: 'SettingsPage', action: 'regenerateKey' });
      showToast('Failed to regenerate API key. Please try again.', 'error');
    } finally {
      setIsLoading(false);
      setSelectedKeyForRegenerate(null);
    }
  }, [selectedKeyForRegenerate, handleError, fetchApiKeys, showToast]);

  // Download CSV
  const handleDownloadCsv = useCallback(() => {
    if (!generatedKey) return;

    // Create CSV content
    const headers = ['Organization ID', 'Client ID', 'Client Secret', 'App Identifier'];
    const row = [
      generatedKey.organizationId,
      generatedKey.client_id,
      generatedKey.client_secret || '',
      generatedKey.appIdentifier,
    ];

    const csvContent = [
      headers.join(','),
      row.map(field => `"${field}"`).join(','),
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'Key_Details.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Close modal after download
    setShowModal(false);
  }, [generatedKey]);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setGeneratedKey(null);
  }, []);

  return (
    <div className="equal-ds-root h-full flex flex-col">
      {/* Page Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-text-primary tracking-wide">
          App API Keys
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Manage your application API keys and credentials
        </p>
      </div>

      {/* Organization ID Display */}
      {apiKeys.length > 0 && apiKeys[0]?.organizationId && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-text-secondary">Organization ID:</span>
          <span className="text-sm font-medium text-text-primary">{apiKeys[0].organizationId}</span>
          <button
            onClick={handleCopyOrgId}
            className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Copy Organization ID"
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Content */}
      {showGenerateButton ? (
        // Show generate button when no keys exist
        <div className="flex-1 flex items-center justify-center">
          <Button
            variant="primary"
            size="lg"
            onClick={handleGenerateKey}
            disabled={isLoading}
          >
            {isLoading ? 'Generating...' : 'Generate Key'}
          </Button>
        </div>
      ) : (
        // Show table when keys exist
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="mb-4">
            <ApiKeysTable
              data={apiKeys}
              onRegenerate={handleRegenerateKey}
              showActions={showActions}
              isLoading={isLoading}
            />
          </div>
        </div>
      )}

      {/* Modal */}
      <ApiKeyModal
        isOpen={showModal}
        onClose={handleCloseModal}
        apiKeyData={generatedKey}
        onDownloadCsv={handleDownloadCsv}
      />

      {/* API Key Rotation: Confirmation Dialog */}
      {showConfirmDialog && selectedKeyForRegenerate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 animate-slideIn">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Regenerate API Key?</h3>
              </div>
              <button
                onClick={handleCancelRegenerate}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to regenerate the API key for this application?
              </p>

              <div className="bg-gray-50 rounded-md p-4 mb-4">
                <p className="text-sm font-medium text-gray-900 mb-1">App Identifier</p>
                <p className="text-sm text-blue-600">{selectedKeyForRegenerate.appIdentifier}</p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <p className="text-sm font-medium text-yellow-800 mb-2">What will happen:</p>
                <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                  <li>A new API key will be generated with a {apiKeyConfig.default_validity_days}-day validity</li>
                  <li>Current key will remain valid for {apiKeyConfig.grace_period_days} days (grace period)</li>
                  <li>After grace period, the old key will stop working</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <Button
                variant="secondary"
                size="md"
                onClick={handleCancelRegenerate}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleConfirmRegenerate}
                disabled={isLoading}
              >
                {isLoading ? 'Regenerating...' : 'Yes, Regenerate'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 animate-slideIn">
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg ${
              toast.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-600" />
            )}
            <p
              className={`text-sm font-medium ${
                toast.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}
            >
              {toast.message}
            </p>
            <button
              onClick={() => setToast(null)}
              className={`ml-2 p-1 rounded hover:bg-opacity-20 ${
                toast.type === 'success' ? 'hover:bg-green-600' : 'hover:bg-red-600'
              }`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
